import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { treatmentKindLabel } from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { LensRow } from './PriceBooks.js'

/**
 * Which coatings a lens can carry. §6.4 `CAT-013`.
 *
 * **`treatment_compatibility` had no writer anywhere, not even in `tools/seed`**, and the read is
 * an allow-list: `lens-choice.ts` says "a missing row blocks... the lab cannot apply what nobody has
 * said it can". So a shop could put an AR coating on file and price it and **still never sell it**,
 * because no lens admitted it. This is the screen that completes that chain.
 *
 * **A no needs a reason, and the reason is the point of the field.** The database requires it and
 * the constraint's own comment says why: an unexplained no becomes a phone call to the lab. A
 * dispenser told "not offered on 1.74 by this supplier" tells the customer and offers the 1.67.
 *
 * **Saying yes is also a publish requirement**, which is not obvious from the form, so the panel
 * says it: Appendix V.4 step 5 blocks a publish for any treatment named by a compatibility rule
 * that has no price rule.
 *
 * **Changing an answer sends the version it was read at** (`ARC-012`), because a supplier changes
 * their mind and an answer recordable once and never correctable is a trap.
 */

export interface CompatibilityRow {
  readonly treatmentId: string
  readonly kind: string
  readonly brand: string | null
  readonly grade: string | null
  readonly colour: string | null
  readonly allowed: boolean
  readonly reason: string | null
  readonly version: number
}

export interface TreatmentRow {
  readonly treatmentId: string
  readonly kind: string
  readonly brand: string | null
  readonly grade: string | null
  readonly colour: string | null
}

export interface LensTreatmentsProps {
  /** Appendix B's `Catalogue - edit`, owner and manager. */
  readonly canEdit: boolean
  readonly loadLenses: () => Promise<readonly LensRow[]>
  readonly loadTreatments: () => Promise<readonly TreatmentRow[]>
  readonly loadCompatibility: (lensProductId: string) => Promise<readonly CompatibilityRow[]>
  readonly onWritten?: () => void
}

/** A coating in words, so a chooser row is identifiable without reading an id. */
const describe = (
  t: (key: never) => string,
  row: { readonly kind: string; readonly brand: string | null; readonly grade: string | null; readonly colour: string | null },
): string =>
  [treatmentKindLabel(t as never, row.kind), row.brand, row.grade, row.colour]
    .filter((part) => part !== null && part !== '')
    .join(' ')

export function LensTreatments({
  canEdit,
  loadLenses,
  loadTreatments,
  loadCompatibility,
  onWritten,
}: LensTreatmentsProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [lenses, setLenses] = useState<readonly LensRow[]>([])
  const [treatments, setTreatments] = useState<readonly TreatmentRow[]>([])
  const [answers, setAnswers] = useState<readonly CompatibilityRow[]>([])
  const [lensProductId, setLensProductId] = useState('')
  const [treatmentId, setTreatmentId] = useState('')
  const [allowed, setAllowed] = useState(true)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all([loadLenses(), loadTreatments()])
      .then(([l, tr]) => {
        if (cancelled) return
        setLenses(l)
        setTreatments(tr)
      })
      .catch(() => {
        // A failed read must not take the form with it: the point of this panel is that a shop with
        // no answers recorded has to be able to record the first one.
        if (!cancelled) {
          setLenses([])
          setTreatments([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [loadLenses, loadTreatments])

  const refreshAnswers = useCallback(async () => {
    if (lensProductId === '') {
      setAnswers([])
      return
    }
    try {
      setAnswers(await loadCompatibility(lensProductId))
    } catch {
      setAnswers([])
    }
  }, [lensProductId, loadCompatibility])

  useEffect(() => {
    void refreshAnswers()
  }, [refreshAnswers])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDone(t('lensTreatments.saved'))
    setReason('')
    void refreshAnswers()
    onWritten?.()
  })

  const save = useCallback(async () => {
    if (lensProductId === '' || treatmentId === '') {
      setError(t('lensTreatments.pickBoth'))
      return
    }
    if (!allowed && reason.trim() === '') {
      // The database requires it too, and the constraint's comment says why: an unexplained no
      // becomes a phone call to the lab.
      setError(t('lensTreatments.reasonRequired'))
      return
    }
    setError(null)
    setDone(null)
    const existing = answers.find((a) => a.treatmentId === treatmentId)
    const write = (await enqueue(
      existing === undefined
        ? {
            method: 'POST',
            path: `/catalogue/lenses/${lensProductId}/treatments`,
            label: t('lensTreatments.queueLabel'),
            body: { treatmentId, allowed, ...(allowed ? {} : { reason: reason.trim() }) },
          }
        : {
            /**
             * An answer already exists for this pair, so this is a change and carries the version
             * it was read at (`ARC-012`). Chosen here rather than letting the server answer 409,
             * because a shopkeeper correcting a coating has not done anything wrong.
             */
            method: 'PATCH',
            path: `/catalogue/lenses/${lensProductId}/treatments/${treatmentId}`,
            label: t('lensTreatments.queueChange'),
            body: {
              allowed,
              expectedVersion: existing.version,
              ...(allowed ? {} : { reason: reason.trim() }),
            },
          },
    )) as { id: string }
    setWriteId(write.id)
  }, [allowed, answers, enqueue, lensProductId, reason, t, treatmentId])

  if (!canEdit) {
    return (
      <section className="lm-setup-panel" aria-labelledby="lens-treatments-heading">
        <h2 id="lens-treatments-heading" className="lm-setup-panel__heading">
          {t('lensTreatments.heading')}
        </h2>
        <p className="lm-hint">{t('lensTreatments.notYours')}</p>
      </section>
    )
  }

  return (
    <section className="lm-setup-panel" aria-labelledby="lens-treatments-heading">
      <h2 id="lens-treatments-heading" className="lm-setup-panel__heading">
        {t('lensTreatments.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('lensTreatments.means')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensTreatments.lens')}</span>
        <select
          className="lm-input"
          value={lensProductId}
          onChange={(e) => setLensProductId(e.currentTarget.value)}
        >
          <option value="">{t('lensTreatments.pickALens')}</option>
          {lenses.map((l) => (
            <option key={l.lensProductId} value={l.lensProductId}>
              {`${l.brand} ${l.name} ${l.refractiveIndex}`}
            </option>
          ))}
        </select>
      </label>

      {lensProductId === '' ? null : answers.length === 0 ? (
        // The state every lens on every deployment was in, said plainly: an empty allow-list means
        // no coating can be put on this lens at all.
        <p className="lm-hint">{t('lensTreatments.noneYet')}</p>
      ) : (
        <ul className="lm-location-list">
          {answers.map((a) => (
            <li key={a.treatmentId} className="lm-location-row">
              <span className="lm-location-row__name" dir="auto">
                {describe(t as never, a)}
              </span>
              {/* A word, never a colour: `CL-02`, and whether a coating can be sold is too large a
                  thing to say with a swatch. */}
              <span className="lm-location-row__meta">
                {a.allowed ? t('lensTreatments.yes') : t('lensTreatments.no')}
              </span>
              {a.reason === null ? null : (
                <span className="lm-location-row__meta" dir="auto">
                  {a.reason}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <label className="lm-field">
        <span className="lm-field__label">{t('lensTreatments.treatment')}</span>
        <select
          className="lm-input"
          value={treatmentId}
          onChange={(e) => setTreatmentId(e.currentTarget.value)}
        >
          <option value="">{t('lensTreatments.pickATreatment')}</option>
          {treatments.map((tr) => (
            <option key={tr.treatmentId} value={tr.treatmentId}>
              {describe(t as never, tr)}
            </option>
          ))}
        </select>
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensTreatments.canItGoOn')}</span>
        <select
          className="lm-input"
          value={allowed ? 'yes' : 'no'}
          onChange={(e) => setAllowed(e.currentTarget.value === 'yes')}
        >
          <option value="yes">{t('lensTreatments.yes')}</option>
          <option value="no">{t('lensTreatments.no')}</option>
        </select>
      </label>

      {allowed ? (
        // Not obvious from the form, so it is said: V.4 step 5 blocks a publish for any treatment
        // named by a compatibility rule that has no price rule.
        <p className="lm-setup-panel__effects">{t('lensTreatments.yesMeans')}</p>
      ) : (
        <label className="lm-field">
          <span className="lm-field__label">{t('lensTreatments.reason')}</span>
          <textarea
            className="lm-input"
            dir="auto"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </label>
      )}

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save()
        }}
      >
        {answers.some((a) => a.treatmentId === treatmentId)
          ? t('lensTreatments.change')
          : t('lensTreatments.save')}
      </button>

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {writeId === null ? null : (
        <p className="lm-hint" role="status">
          {statusLine(t, snapshot, writeId, t('lensTreatments.queueSubject'))}
        </p>
      )}
    </section>
  )
}
